import React from "react";
import { Box, Divider, TextField } from "@mui/material";
import { SimpleFormLabel } from "components/dialogs/SimpleFormDialog";
import { IntervalSelector } from "components/queries/queryBuilder/dialogParts/IntervalSelector";
import { QueryFieldSelector } from "components/queries/queryBuilder/dialogParts/QueryFieldSelector";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { QueryField, QueryInfo } from "system/query/QueryInfo";
import LeafAggSelector from "./LeafAggSelector";
import { Agg } from "system/query/Aggs";

export interface HistogramAggSelectorProps {
  queryInfo: QueryInfo;

  aggs?: Agg;
  setAggs: (newAggs: Agg) => void;

  additionalLeafAggProps?: any;

  allowMultipleKeywordSelection?: boolean;
  disableKeywordFilter?: boolean;
  allowKeywordAgg?: boolean;

  includeTimeOfDayAndDayOfWeekOptions?: boolean;
}

export default function HistogramAggSelector(props: HistogramAggSelectorProps): JSX.Element {
  const { i18n } = useTranslation();

  const [selectedHistogramField, setSelectedHistogramField] = useState<QueryField | undefined>();
  const [aggsLabel, setAggsLabel] = useState<string>("");

  // Build options from aggs
  useEffect(() => {
    if (!props.queryInfo?.fields) {
      return;
    }
    if (props.aggs?.field) {
      const histogramField = props.queryInfo.fields?.find((field: QueryField) => field.id === props.aggs?.field);
      setSelectedHistogramField(histogramField);
    } else {
      const timestampFields = props.queryInfo.fields?.filter((field: QueryField) => field.type === "timestamp");
      const firstTimestampField = timestampFields?.[0];
      setSelectedHistogramField(firstTimestampField || "");
      if (firstTimestampField?.id) {
        updateAggs({
          field: firstTimestampField?.id
        });
      }
    }
  }, [props.aggs?.field, props.queryInfo.fields]);

  const buildAggs = (options:any) => {
    const fieldId = options?.field || props.aggs?.field,
        histogramField = props.queryInfo.fields?.find((field: QueryField) => field.id === fieldId);

    const isKeywordAgg = histogramField?.type === "keyword" || histogramField?.type === "keywords";

    const aggs:Agg = {
      op: isKeywordAgg ? "keyword" : "histogram",
      field: fieldId
    }

    let interval = options.interval ?? props.aggs?.interval;
    if (!interval && ["time_of_day", "day_of_week"].includes(props.aggs?.op as string)) {
      interval = props.aggs?.op as string;
    }

    if (isKeywordAgg) {
      aggs.bucket_count = options.bucket_count;
    } else {
      if (interval && ["time_of_day", "day_of_week"].includes(interval)) {
        aggs.op = interval;
        delete aggs.interval;
      } else if (interval && interval !== "__auto__") {
        if (histogramField?.type === "timestamp") {
          aggs.interval = interval;
        } else {
          aggs.interval = interval ? parseInt(interval) : "";
        }
      }
    }

    if (!aggs.interval) {
      delete aggs.interval;
    }

    if (options?.aggs === null) {
      return aggs;
    }

    if (Object.keys(options?.aggs || {}).length || Object.keys(props.aggs?.aggs || {}).length) {
      const subAggs = options?.aggs || props.aggs?.aggs || {};
      const aggKey = Object.keys(subAggs)[0];
      if (subAggs?.[aggKey]) {
        aggs.aggs = subAggs;
      }
      if(isKeywordAgg && aggKey.length && aggKey != "Records") {
        if(["min", "max", "sum", "avg"].includes(subAggs[aggKey].op)) {
          aggs.sort = {
            field: aggKey
          }
        }
      }
    }
    return aggs;
  }

  const originalAggs: any = props.aggs?.aggs || {},
      selectedAggKey = Object.keys(originalAggs)[0],
      selectedAgg = originalAggs?.[selectedAggKey];

  const updateAggs = (options:Agg) => {
    const aggs = buildAggs(options);
    props.setAggs(aggs);
  }

  const validHistogramFields = props.queryInfo.fields?.filter((field: QueryField) => {
    if (field.type !== "integer" && field.type !== "decimal") {
      return true;
    }
    // check if there is a value for both min and max
    if (field.min !== undefined && field.max !== undefined) {
      return true;
    }
  });

  const allowedFieldTypes = props.allowKeywordAgg === true ? ["timestamp", "decimal", "integer", "keyword", "keywords"] :
                                                             ["timestamp", "decimal", "integer"];

  let intervalValue: string = (props.aggs?.interval || "__auto__") as string;
  if (["time_of_day", "day_of_week"].includes(props.aggs?.op as string)) {
    intervalValue = props.aggs?.op as string;
  }

  return (
    <Box>
      <SimpleFormLabel>{i18n.t("x-axis")}</SimpleFormLabel>
      <QueryFieldSelector
        allowedFieldTypes={allowedFieldTypes}
        selectedFieldId={selectedHistogramField?.id || ""}
        requireAggSupported={true}
        setSelectedFieldId={(newFieldId:string) => {
          updateAggs({
            field: newFieldId
          });
        }}
        fields={validHistogramFields}
      />
      {selectedHistogramField?.type === "timestamp" &&
        <>
          <SimpleFormLabel>{i18n.t("interval")}</SimpleFormLabel>
          <IntervalSelector
            selectedInterval={intervalValue}
            includeTimeOfDayAndDayOfWeekOptions={props.includeTimeOfDayAndDayOfWeekOptions}
            setSelectedInterval={(newInterval:string) => {
              updateAggs({
                interval: newInterval
              });
            }}/>
        </>
      }
      {(selectedHistogramField?.type === "keyword" || selectedHistogramField?.type === "keywords") &&
        <>
        <SimpleFormLabel>{i18n.t("buckets")} ({i18n.t("optional")})</SimpleFormLabel>
        <TextField
          autoComplete="off"
          value={props.aggs?.bucket_count !== undefined ? props.aggs?.bucket_count : ""}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            updateAggs({
              bucket_count: event.target.value ? parseInt(event.target.value) : undefined
            });
          }}/>
        </>
      }
      {selectedHistogramField?.type !== "timestamp" && selectedHistogramField?.type !== "keyword" && selectedHistogramField?.type !== "keywords" &&
        <>
          <SimpleFormLabel>{i18n.t("interval")} ({i18n.t("optional")})</SimpleFormLabel>
          <TextField
            autoComplete="off"
            value={props.aggs?.interval ? props.aggs?.interval as string : ""}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              updateAggs({
                interval: event.target.value ? parseInt(event.target.value) : ""
              });
            }}/>
        </>
      }
      <Divider sx={{margin: "10px 0"}} />
      <SimpleFormLabel>{i18n.t("y-axis")}</SimpleFormLabel>
      <LeafAggSelector
        aggs={selectedAgg}
        queryInfo={props.queryInfo}
        requireAggSupported={true}
        aggsLabel={aggsLabel}
        setAggsLabel={setAggsLabel}
        disableKeywordFilter={props.disableKeywordFilter}
        allowedFieldTypes={["decimal", "integer", "timeseries", "keyword", "keywords", "boolean"]}
        setAggs={(newAggs:Agg | null, label?:string):void => {
          if (!newAggs) {
            updateAggs({ aggs: null });
          }
          updateAggs({
            aggs: {
              [label || "subAgg"]: newAggs
            }
          });
        }}
        {...props.additionalLeafAggProps}
        />
    </Box>
  );
}
